// SitePerformance.jsx
// Required dependencies:
// npm install react-chartjs-2 chart.js react-datepicker react-loading-skeleton react-countup react-icons react-table tailwindcss
// Add to tailwind.config.js:
// fontFamily: { roboto: ['Roboto', 'sans-serif'], poppins: ['Poppins', 'sans-serif'] },
// Import 'react-datepicker/dist/react-datepicker.css' in your main file

import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import CountUp from 'react-countup';
import { FaDownload, FaFilter } from 'react-icons/fa';
import { useTable } from 'react-table';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SitePerformance = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedSite, setSelectedSite] = useState('All Sites');
  const [selectedWorkType, setSelectedWorkType] = useState('All Types');
  const [data, setData] = useState(null);

  // Simulate advanced data fetching with filters
  useEffect(() => {
    setLoading(true);
    // In real app, use axios or fetch with query params based on filters
    setTimeout(() => {
      const sampleData = {
        stats: {
          avgTime: 4.2,
          totalVisits: 248,
          itemsDelivered: 1342,
        },
        workCompleted: {
          labels: ['Site A', 'Site B', 'Site C', 'Site D', 'Site E'],
          data: [65, 59, 80, 81, 56],
        },
        workType: {
          labels: ['Delivery', 'Service', 'Installation'],
          data: [45, 30, 25],
        },
        productivity: {
          labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
          data: [65, 59, 80, 81, 56, 72],
        },
        tableData: [
          { site: 'Site A', workCompleted: 65, avgTime: 4.5, visits: 50, items: 300, productivity: 85 },
          { site: 'Site B', workCompleted: 59, avgTime: 3.8, visits: 45, items: 250, productivity: 78 },
          { site: 'Site C', workCompleted: 80, avgTime: 5.2, visits: 60, items: 400, productivity: 92 },
          { site: 'Site D', workCompleted: 81, avgTime: 4.8, visits: 55, items: 350, productivity: 89 },
          { site: 'Site E', workCompleted: 56, avgTime: 3.5, visits: 38, items: 242, productivity: 75 },
        ],
      };
      setData(sampleData);
      setLoading(false);
    }, 1500);
  }, [dateRange, selectedSite, selectedWorkType]);

  // Bar Chart: Work Completed per Site
  const barData = {
    labels: data?.workCompleted.labels || [],
    datasets: [
      {
        label: 'Work Completed',
        data: data?.workCompleted.data || [],
        backgroundColor: '#0EA5E9', // vibrant blue
        borderRadius: 6,
      },
    ],
  };

  // Pie Chart: Work Type Distribution
  const pieData = {
    labels: data?.workType.labels || [],
    datasets: [
      {
        data: data?.workType.data || [],
        backgroundColor: ['#14B8A6', '#8B5CF6', '#10B981'], // teal, purple, soft green
        borderWidth: 0,
        hoverOffset: 12,
      },
    ],
  };

  // Line Chart: Site Productivity Trend
  const lineData = {
    labels: data?.productivity.labels || [],
    datasets: [
      {
        label: 'Productivity Trend',
        data: data?.productivity.data || [],
        borderColor: '#8B5CF6', // purple
        backgroundColor: 'rgba(139, 92, 246, 0.15)',
        tension: 0.4,
        fill: true,
        pointHoverRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { font: { family: 'Poppins', weight: 600 } } },
      tooltip: {
        backgroundColor: 'rgba(255,255,255,0.95)',
        titleColor: '#1F2937',
        bodyColor: '#1F2937',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 6,
      },
    },
    animation: { duration: 1200, easing: 'easeOutQuart' },
    interaction: { mode: 'index', intersect: false },
  };

  // Table columns for detailed data
  const columns = React.useMemo(
    () => [
      { Header: 'Site', accessor: 'site' },
      { Header: 'Work Completed', accessor: 'workCompleted' },
      { Header: 'Avg Time', accessor: 'avgTime' },
      { Header: 'Visits', accessor: 'visits' },
      { Header: 'Items Delivered', accessor: 'items' },
      { Header: 'Productivity (%)', accessor: 'productivity' },
    ],
    []
  );

  const tableInstance = useTable({ columns, data: data?.tableData || [] });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  const handleExport = () => {
    // In real app, use xlsx or csv-export library
    alert('Exporting data...');
  };

  return (
    <div className="min-h-screen bg-gray-50/50 font-roboto text-gray-800 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <h1 className="text-3xl font-poppins font-bold text-gray-900">Site Performance</h1>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px] bg-white hover:border-blue-500 transition-colors"
          >
            <option>All Sites</option>
            <option>Site A</option>
            <option>Site B</option>
            <option>Site C</option>
            <option>Site D</option>
          </select>
          <select
            value={selectedWorkType}
            onChange={(e) => setSelectedWorkType(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px] bg-white hover:border-blue-500 transition-colors"
          >
            <option>All Types</option>
            <option>Delivery</option>
            <option>Service</option>
            <option>Installation</option>
          </select>
          <DatePicker
            selectsRange={true}
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            onChange={(update) => setDateRange(update)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white hover:border-blue-500 transition-colors"
            placeholderText="Select date range"
          />
          <button
            onClick={handleExport}
            className="bg-blue-600 text-white text-sm px-4 py-2 rounded-full hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2 font-poppins font-medium"
          >
            <FaDownload className="hover:scale-110 transition-transform" />
            Export
          </button>
        </div>
      </div>

      <hr className="mb-8 border-gray-200" />

      {/* Stats with Progress Bars */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <p className="text-sm font-poppins font-semibold text-gray-600 mb-2">{stat.title}</p>
            {loading ? (
              <Skeleton width={120} height={28} />
            ) : (
              <p className="text-3xl font-poppins font-bold" style={{ color: stat.color }}>
                <CountUp end={stat.value} duration={1.5} formattingFn={(value) => stat.format ? stat.format(value) : value} />
              </p>
            )}
            <div className="mt-4">
              {loading ? (
                <Skeleton height={8} />
              ) : (
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${stat.progress}%`, backgroundColor: stat.color }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        {/* Bar Chart */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <h2 className="text-lg font-poppins font-semibold mb-4">Work Completed per Site</h2>
          <div className="h-[320px]">
            {loading ? <Skeleton height="100%" /> : <Bar data={barData} options={chartOptions} />}
          </div>
        </div>

        {/* Pie Chart */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <h2 className="text-lg font-poppins font-semibold mb-4">Work Type Distribution</h2>
          <div className="h-[320px] flex items-center justify-center">
            {loading ? <Skeleton circle height={240} width={240} /> : <Pie data={pieData} options={{ ...chartOptions, cutout: '30%' }} />}
          </div>
        </div>

        {/* Line Chart */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow lg:col-span-2">
          <h2 className="text-lg font-poppins font-semibold mb-4">Site Productivity Trend</h2>
          <div className="h-[320px]">
            {loading ? <Skeleton height="100%" /> : <Line data={lineData} options={chartOptions} />}
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <h2 className="text-lg font-poppins font-semibold p-6">Detailed Site Data</h2>
        {loading ? (
          <Skeleton count={5} height={48} />
        ) : (
          <table {...getTableProps()} className="w-full">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()} className="border-b border-gray-200 bg-gray-50">
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()} className="text-left py-3 px-6 text-sm font-poppins font-semibold text-gray-600">
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="py-3 px-6 text-sm">
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const stats = [
  { title: 'Avg Time per Site', value: 4.2, color: '#0EA5E9', format: (v) => `${v}h`, progress: 75 },
  { title: 'Total Visits', value: 248, color: '#14B8A6', progress: 90 },
  { title: 'Items Delivered', value: 1342, color: '#8B5CF6', progress: 85 },
];

export default SitePerformance;